import { useState } from 'react';
import type { BrandLogo as Logo } from '@kaba/shared';
import { cx } from './ui/ui';
import { useThemeMode } from '../lib/theme';

/**
 * Logo mit reserviertem Platz (kein Layoutsprung, V03). Im Dunkeln die dunkle Variante, sonst das
 * Hauptlogo auf heller, neutraler Fläche – nie plötzlich ein unpassendes Bild. Am Handy das Signet,
 * falls vorhanden. Fehlerzustand: neutrales Initialenfeld.
 */
export function BrandLogo({
  logo,
  name,
  size = 'header',
  className,
  mode: forced,
}: {
  logo: Logo;
  /** Vorschau: Modus fest vorgeben */
  mode?: 'light' | 'dark';
  /** Für den Alternativtext und das Ersatzzeichen */
  name: string;
  size?: 'header' | 'tile' | 'preview-desktop' | 'preview-mobile';
  className?: string;
}) {
  const sys = useThemeMode();
  const mode = forced ?? sys;
  const [failed, setFailed] = useState<string | null>(null);
  const dims = {
    header: { h: 32, max: 160, hm: 26, maxm: 80 },
    tile: { h: 20, max: 72, hm: 20, maxm: 72 },
    'preview-desktop': { h: 32, max: 160, hm: 32, maxm: 160 },
    'preview-mobile': { h: 26, max: 80, hm: 26, maxm: 80 },
  }[size];
  const useMarkOnPhone = size === 'header' || size === 'preview-mobile';
  const mark = useMarkOnPhone && logo.mark ? { src: logo.mark, ratio: logo.markRatio ?? 1 } : null;
  const main = mode === 'dark' && logo.dark ? logo.dark : logo.main;
  const plate = mode === 'dark' && !logo.dark;

  const img = (src: string, ratio: number, h: number, max: number, cls?: string) => {
    const w = Math.min(max, Math.round(h * ratio));
    if (failed === src)
      return (
        <span
          className={cx('inline-flex shrink-0 items-center justify-center rounded-md bg-[var(--column)] text-xs font-semibold text-[var(--ink-soft)]', cls)}
          style={{ height: h, width: Math.max(h, Math.min(w, h * 1.5)) }}
          role="img"
          aria-label={name}
        >
          {initials(name)}
        </span>
      );
    return (
      <span className={cx('inline-flex shrink-0 items-center justify-center', plate && 'rounded-md bg-white px-1', cls)} style={{ height: h + (plate ? 4 : 0) }}>
        <img src={src} alt={name} width={w} height={Math.round(w / ratio)} style={{ maxHeight: h, maxWidth: max, width: 'auto', height: 'auto' }} className="block object-contain" onError={() => setFailed(src)} />
      </span>
    );
  };

  if (size === 'preview-mobile' && mark) return <span className={className}>{img(mark.src, mark.ratio, dims.hm, dims.maxm)}</span>;
  if (size !== 'header') return <span className={className}>{img(main, logo.ratio, dims.h, dims.max)}</span>;
  return (
    <span className={cx('inline-flex', className)}>
      {mark ? (
        <>
          {img(mark.src, mark.ratio, dims.hm, dims.maxm, 'sm:hidden')}
          {img(main, logo.ratio, dims.h, dims.max, 'max-sm:hidden')}
        </>
      ) : (
        <>
          {img(main, logo.ratio, dims.hm, dims.maxm, 'sm:hidden')}
          {img(main, logo.ratio, dims.h, dims.max, 'max-sm:hidden')}
        </>
      )}
    </span>
  );
}

export function initials(name: string): string {
  const w = name.trim().split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? '') + (w[1]?.[0] ?? '')).toUpperCase() || 'K';
}
